/*
================================================================================
FILE HEADER — packages/healing/src/index.ts
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why                      | What Changed         |
|----|------------|-------------------|--------------------------|----------------------|
| 1  | 2026-06-25 | Claude Sonnet 4.6 | Phase 2 Tier-1 healing   | Created — ONNX       |
|    |            |                   |                          | re-ranking engine    |
|    |            |                   |                          | using all-MiniLM-L6  |

FILES THAT IMPORT THIS FILE
-----------------------------
- packages/runner/src/resolver.ts   (Tier-0 fallback)
- packages/cli/src/plex.ts          (heal commands)

EXPORTS COVERED
---------------
- HealCandidate     — one ranked A11yNode candidate with score breakdown
- HealResult        — full output of reRankCandidates()
- reRankCandidates  — main healing API: takes Selector + ARIA nodes → HealResult
- getEmbedder       — lazy singleton embedder (lazy-loads the ONNX model)

IMPORTANT POINTS
----------------
1. The ONNX model (all-MiniLM-L6-v2, ~23MB quantized) is loaded lazily on
   first call to getEmbedder(). Subsequent calls reuse the singleton.
   Model cache: ~/.plex/models/ (Node) or browser OPFS (browser WASM).

2. Composite score formula (from architecture spec):
     0.45 × cosine(semantic embeddings)
     0.25 × geometry (bounds ratio on viewport)
     0.20 × attribute exact match
     0.10 × neighbor overlap
   accept: top1 score ≥ minConfidence AND margin ≥ 0.07

3. Same-screen gate: Jaccard similarity of visible text hashes must be ≥ 0.35
   between the fingerprint's screenSignature and the current screen before
   Tier-1 re-ranking is attempted.

4. This package works in BOTH browser (WASM backend) and Node.js.
   @huggingface/transformers auto-selects the backend.

5. SHARED with the sister mobile platform — do not add web-specific logic here.
================================================================================
*/
let _embedder = null;
let _loading = null;
export async function getEmbedder() {
    if (_embedder)
        return _embedder;
    if (_loading)
        return _loading;
    _loading = (async () => {
        const { pipeline, env } = await import('@huggingface/transformers');
        // In Node.js, cache models in ~/.plex/models/ to avoid re-downloads
        if (typeof process !== 'undefined' && process.env) {
            const { homedir } = await import('os');
            const { join } = await import('path');
            env.cacheDir = join(homedir(), '.plex', 'models');
        }
        // quantized=true uses the INT8 ONNX model (~23MB instead of ~90MB)
        const extractor = await pipeline('feature-extraction', 'Xenova/all-MiniLM-L6-v2', { dtype: 'q8' });
        const fn = async (text) => {
            const output = await extractor(text, { pooling: 'mean', normalize: true });
            // @huggingface/transformers 3.x returns a Tensor with .data
            const data = output.data;
            return data instanceof Float32Array ? data : new Float32Array(data);
        };
        _embedder = fn;
        return fn;
    })();
    return _loading;
}
/**
 * Re-rank all visible ARIA nodes against a broken Selector using Tier-1 ONNX.
 *
 * Returns HealResult with topCandidate and accepted flag.
 * The caller decides whether to ACT (mode=auto) or SUGGEST (mode=suggest).
 */
export async function reRankCandidates(selector, nodes, opts = {}) {
    const { minConfidence = 0.60, minMargin = 0.07, viewportWidth = 1280, viewportHeight = 800, maxTopK = 3, } = opts;
    const fp = selector.fingerprint;
    // ── Same-screen gate ─────────────────────────────────────────────────────
    const sameScreenGate = fp ? checkSameScreen(fp, nodes) : true;
    // ── Semantic query text ───────────────────────────────────────────────────
    const queryText = fp?.semanticText ?? selector.description;
    let embed;
    try {
        embed = await getEmbedder();
    }
    catch {
        // Model failed to load — return no result
        return { topCandidate: null, allCandidates: [], accepted: false, confidence: 'none', sameScreenGate };
    }
    const queryVec = await embed(queryText);
    // ── Score every visible, clickable node ──────────────────────────────────
    const clickableNodes = nodes.filter(n => n.clickable && n.enabled);
    const scored = await Promise.all(clickableNodes.map(async (node) => {
        const nodeText = buildNodeSemanticText(node);
        const nodeVec = await embed(nodeText);
        const semantic = cosineSimilarity(queryVec, nodeVec);
        const geometry = fp ? geometryScore(node.bounds, fp.boundsRatio, viewportWidth, viewportHeight) : 0;
        const attribute = attributeMatchScore(node, selector);
        const neighbor = fp ? neighborOverlapScore(node, fp.neighbors, nodes) : 0;
        const score = 0.45 * semantic +
            0.25 * geometry +
            0.20 * attribute +
            0.10 * neighbor;
        const candidate = buildCandidate(node);
        return { node, score, breakdown: { semantic, geometry, attribute, neighbor }, candidate };
    }));
    scored.sort((a, b) => b.score - a.score);
    const top = scored.slice(0, maxTopK);
    const top1 = top[0] ?? null;
    const top2 = top[1] ?? null;
    const margin = top1 && top2 ? top1.score - top2.score : 1;
    const accepted = !!top1 && top1.score >= minConfidence && margin >= minMargin;
    const confidence = !top1 ? 'none'
        : top1.score > 0.8 ? 'high'
            : top1.score > 0.6 ? 'medium'
                : 'low';
    return {
        topCandidate: top1,
        allCandidates: top,
        accepted,
        confidence,
        sameScreenGate,
    };
}
// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function buildNodeSemanticText(node) {
    const parts = [];
    if (node.role)
        parts.push(node.role);
    if (node.text)
        parts.push(node.text);
    if (node.contentDesc)
        parts.push(node.contentDesc);
    if (node.placeholder)
        parts.push(node.placeholder);
    if (node.resourceId)
        parts.push(node.resourceId);
    return parts.join(' ').trim() || 'unknown';
}
function cosineSimilarity(a, b) {
    let dot = 0, normA = 0, normB = 0;
    for (let i = 0; i < a.length; i++) {
        dot += a[i] * b[i];
        normA += a[i] * a[i];
        normB += b[i] * b[i];
    }
    const denom = Math.sqrt(normA) * Math.sqrt(normB);
    return denom === 0 ? 0 : Math.max(0, Math.min(1, dot / denom));
}
function geometryScore(bounds, boundsRatio, vw, vh) {
    // Current position as ratio
    const cx = (bounds.x + bounds.width / 2) / vw;
    const cy = (bounds.y + bounds.height / 2) / vh;
    const cw = bounds.width / vw;
    const ch = bounds.height / vh;
    // Expected position from fingerprint
    const [ex, ey, ew, eh] = boundsRatio;
    const dx = Math.abs(cx - (ex + ew / 2));
    const dy = Math.abs(cy - (ey + eh / 2));
    const dw = Math.abs(cw - ew);
    const dh = Math.abs(ch - eh);
    const dist = (dx + dy + dw + dh) / 4;
    return Math.max(0, 1 - dist * 4); // score→0 as distance grows
}
function attributeMatchScore(node, selector) {
    // Check if any candidate's value matches node attributes exactly
    for (const c of selector.candidates) {
        if (c.strategy === 'text' && node.text === c.value)
            return 1;
        if (c.strategy === 'contentDesc' && node.contentDesc === c.value)
            return 1;
        if (c.strategy === 'a11yId' && node.resourceId === c.value)
            return 1;
    }
    return 0;
}
function neighborOverlapScore(node, neighbors, allNodes) {
    if (!neighbors)
        return 0;
    const bounds = node.bounds;
    const cx = bounds.x + bounds.width / 2;
    const cy = bounds.y + bounds.height / 2;
    // Count which neighbor directions exist in current screen
    const directions = ['above', 'below', 'left', 'right'];
    let matches = 0;
    for (const dir of directions) {
        const expected = neighbors[dir];
        if (!expected)
            continue;
        const nearNodes = allNodes.filter((n) => {
            if (!n.text && !n.contentDesc)
                return false;
            const ncx = n.bounds.x + n.bounds.width / 2;
            const ncy = n.bounds.y + n.bounds.height / 2;
            if (dir === 'above' && ncy < cy && Math.abs(ncx - cx) < bounds.width)
                return true;
            if (dir === 'below' && ncy > cy && Math.abs(ncx - cx) < bounds.width)
                return true;
            if (dir === 'left' && ncx < cx && Math.abs(ncy - cy) < bounds.height)
                return true;
            if (dir === 'right' && ncx > cx && Math.abs(ncy - cy) < bounds.height)
                return true;
            return false;
        });
        const found = nearNodes.some(n => (n.text && n.text.includes(expected)) ||
            (n.contentDesc && n.contentDesc.includes(expected)));
        if (found)
            matches++;
    }
    return directions.length > 0 ? matches / directions.length : 0;
}
function checkSameScreen(fp, nodes) {
    // Collect visible text hashes from current screen
    const currentTexts = new Set();
    for (const n of nodes) {
        if (n.text)
            currentTexts.add(n.text.toLowerCase().trim());
        if (n.contentDesc)
            currentTexts.add(n.contentDesc.toLowerCase().trim());
    }
    // Parse fingerprint screenSignature (comma-separated text hashes)
    const fpTexts = new Set(fp.screenSignature.split(',').filter(Boolean));
    // Jaccard similarity
    let intersection = 0;
    for (const t of fpTexts) {
        if (currentTexts.has(t))
            intersection++;
    }
    const union = fpTexts.size + currentTexts.size - intersection;
    if (union === 0)
        return true;
    return (intersection / union) >= 0.35;
}
function buildCandidate(node) {
    // Prefer stable strategies in priority order
    if (node.resourceId) {
        return { strategy: 'a11yId', value: node.resourceId, addedBy: 'heal' };
    }
    if (node.contentDesc) {
        return { strategy: 'contentDesc', value: node.contentDesc, addedBy: 'heal' };
    }
    if (node.text) {
        return { strategy: 'text', value: node.text, addedBy: 'heal' };
    }
    if (node.role) {
        return { strategy: 'className', value: `${node.role}[${node.text ?? ''}]`, addedBy: 'heal' };
    }
    return { strategy: 'text', value: 'unknown', addedBy: 'heal' };
}
//# sourceMappingURL=index.js.map